import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ShoppingBag } from "lucide-react";
import { STREAMING, MERCH } from "../data/content";

export const Merch = () => {
  return (
    <section
      id="merch"
      data-testid="merch-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#050505] overflow-hidden"
    >
      <div
        className="spotlight"
        style={{
          width: 600,
          height: 600,
          background: "rgba(245,158,11,0.10)",
          top: "10%",
          left: "5%",
        }}
      />
      <div
        className="spotlight"
        style={{
          width: 500,
          height: 500,
          background: "rgba(212,175,55,0.08)",
          bottom: "5%",
          right: "5%",
        }}
      />

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12"
        >
          <div>
            <div className="flex items-center gap-4 mb-4">
              <span className="h-px w-10 bg-[color:var(--gold)]" />
              <span className="overline">The Collection</span>
            </div>
            <h2 className="font-condensed text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              Wear the <span className="font-display italic gold-text">legacy.</span>
            </h2>
            <p className="mt-5 text-white/60 max-w-md">
              Handpicked drops to carry the sound — hoodies, vinyls, tees, posters,
              and signature pieces.
            </p>
          </div>
          <a
            href={STREAMING.etsy}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="shop-collection-cta"
            className="btn-gold"
          >
            <ShoppingBag size={16} strokeWidth={2} />
            Shop the Collection
            <ArrowUpRight size={14} />
          </a>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {MERCH.map((m, i) => (
            <motion.a
              key={m.name}
              href={STREAMING.etsy}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`merch-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="group block relative"
            >
              {/* Number chip */}
              <span className="absolute -top-3 left-4 z-20 font-display italic text-xs gold-text bg-[#050505] px-2">
                No. 0{i + 1}
              </span>

              <div
                className="relative aspect-[3/4] overflow-hidden rounded-xl border border-white/10 group-hover:border-[color:var(--gold)]/50 transition-all duration-700"
                style={{ backgroundColor: m.bg || "#0a0a0a" }}
              >
                {/* Signature spotlight — activates on hover */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none z-10"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 40%, rgba(245,158,11,0.28), transparent 55%)",
                  }}
                />

                {/* Clipped corner frame */}
                <div className="absolute top-3 left-3 w-6 h-6 border-t border-l border-[color:var(--gold)]/60 z-10 pointer-events-none" />
                <div className="absolute top-3 right-3 w-6 h-6 border-t border-r border-[color:var(--gold)]/60 z-10 pointer-events-none" />
                <div className="absolute bottom-3 left-3 w-6 h-6 border-b border-l border-[color:var(--gold)]/60 z-10 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="absolute bottom-3 right-3 w-6 h-6 border-b border-r border-[color:var(--gold)]/60 z-10 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                <img
                  src={m.img}
                  alt={m.name}
                  className={`w-full h-full transition-all duration-1000 group-hover:scale-[1.06] ${
                    m.fit === "contain" ? "object-contain p-6" : "object-cover"
                  }`}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

                {/* Type chip bottom-left */}
                <span className="absolute bottom-4 left-4 z-10 overline">
                  {m.type}
                </span>

                {/* Arrow chip top-right on hover */}
                <span className="absolute bottom-4 right-4 z-10 w-10 h-10 rounded-full bg-[color:var(--gold)] text-black grid place-items-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                  <ArrowUpRight size={16} />
                </span>
              </div>

              <div className="mt-5 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-display text-lg sm:text-xl leading-tight">
                    {m.name}
                  </p>
                  <p className="gold-text text-sm mt-1 font-mono tracking-wider">
                    {m.price}
                  </p>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-10 text-center"
        >
          <a
            href={STREAMING.etsy}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="shop-url-link"
            className="text-xs uppercase tracking-[0.22em] text-white/55 hover:text-[color:var(--gold)] transition-colors link-underline"
          >
            View full shop — minhizeeshop.etsy.com →
          </a>
        </motion.div>
      </div>
    </section>
  );
};
